import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Order, OrderShort } from '~/api/Order';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { cartStore, mainStore } from '~/stores';
import { Product } from '~/stores/CategoriesStore';

interface UseReorderOptions {
  skipNavigation?: boolean;
}

export const useReorder = (
  order: Order | OrderShort | null | undefined,
  options?: UseReorderOptions,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isReorderLoading, setIsReorderLoading] = useState(false);

  const handleReorder = useCallback(async () => {
    if (!order?.items?.length) {
      return;
    }

    setIsReorderLoading(true);

    try {
      const reorderableProducts = order.items.filter(
        (i) => !i.is_gift && !i.bundle_id,
      );
      const reorderableBundles = order.bundles ?? [];

      const products = await Promise.all(
        reorderableProducts.map((item) => Product.requestById(item.product_id)),
      );
      const bundleProducts = await Promise.all(
        reorderableBundles.map((item) =>
          Product.requestById(item.catalog_bundle_id),
        ),
      );

      cartStore.emptyCart();

      reorderableProducts.forEach((item, index) => {
        const product = products[index];
        const count = Math.max(
          item.actual_quantity ?? item.picked_quantity ?? 1,
          1,
        );

        if (!product) {
          throw new Error();
        }

        const offer =
          product.sku === item.sku
            ? product
            : product.offers.find(({ sku }) => sku === item.sku);

        if (!offer) {
          throw new Error();
        }

        cartStore.setCartItemCountByProduct(
          offer,
          count,
          'add',
          'product_main',
        );
      });

      reorderableBundles.forEach((item, index) => {
        const product = bundleProducts[index];
        const count = Math.max(item.quantity ?? 1, 1);

        if (!product) {
          throw new Error();
        }

        cartStore.setCartItemCountByProduct(
          product,
          count,
          'add',
          'product_main',
        );
      });

      analyticsService.track(AnalyticsEvent.REORDER_BUTTON_CLICKED, {
        orderId: order.id,
      });

      if (!options?.skipNavigation) {
        navigate('/cart');
      }
    } catch (error) {
      mainStore.pushAlert('error', t('errors:oops'));
      console.error(error);
      cartStore.emptyCart();
    } finally {
      setIsReorderLoading(false);
    }
  }, [order, navigate, t]);

  return { handleReorder, isReorderLoading };
};
